/**
 * Centralized Logger Utility
 *
 * Provides a unified logging interface with debug toggle for production.
 * In production (debug: false), only warnings and errors are logged.
 * In development (debug: true), all log levels are shown.
 *
 * @module utils/logger
 */

import { redactSecrets } from './log-redaction';

// ============================================================
// Types
// ============================================================

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LoggerConfig {
  /** Enable debug logging (default: false for production) */
  debug: boolean;
  /** Minimum log level to display (default: 'warn' in production, 'debug' in debug mode) */
  minLevel?: LogLevel;
  /** Prefix for all log messages (default: '[AppBouncer]') */
  prefix?: string;
  /** Auto-redact sensitive data in objects (default: true) */
  redactSensitiveData?: boolean;
}

// ============================================================
// Constants
// ============================================================

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const DEFAULT_PREFIX = '[AppBouncer]';

// ============================================================
// Logger State
// ============================================================

let loggerConfig: LoggerConfig = {
  debug: false,
  minLevel: 'warn',
  prefix: DEFAULT_PREFIX,
  redactSensitiveData: true,
};

// ============================================================
// Configuration Functions
// ============================================================

/**
 * Configure the logger settings.
 *
 * @param config - Partial configuration to merge
 *
 * @example
 * ```typescript
 * // Enable debug logging
 * configureLogger({ debug: true });
 *
 * // Production mode (only warnings and errors)
 * configureLogger({ debug: false });
 * ```
 */
export function configureLogger(config: Partial<LoggerConfig>): void {
  loggerConfig = {
    ...loggerConfig,
    ...config,
  };

  // Auto-set minLevel based on debug flag if not explicitly set
  if (config.debug !== undefined && config.minLevel === undefined) {
    loggerConfig.minLevel = config.debug ? 'debug' : 'warn';
  }
}

/**
 * Get current logger configuration.
 */
export function getLoggerConfig(): Readonly<LoggerConfig> {
  return { ...loggerConfig };
}

/**
 * Check if debug mode is enabled.
 */
export function isDebugEnabled(): boolean {
  return loggerConfig.debug;
}

// ============================================================
// Core Logging Functions
// ============================================================

/**
 * Check if a log level should be displayed based on current config.
 */
function shouldLog(level: LogLevel): boolean {
  const currentMinLevel = loggerConfig.minLevel || (loggerConfig.debug ? 'debug' : 'warn');
  return LOG_LEVELS[level] >= LOG_LEVELS[currentMinLevel];
}

/**
 * Format and sanitize log arguments.
 */
function formatArgs(args: unknown[]): unknown[] {
  if (!loggerConfig.redactSensitiveData) {
    return args;
  }

  return args.map((arg) => {
    if (typeof arg === 'object' && arg !== null) {
      return redactSecrets(arg as Record<string, unknown>);
    }
    return arg;
  });
}

/**
 * Internal log function.
 */
function logInternal(level: LogLevel, message: string, ...args: unknown[]): void {
  if (!shouldLog(level)) {
    return;
  }

  const prefix = loggerConfig.prefix || DEFAULT_PREFIX;
  const timestamp = new Date().toISOString();
  const formattedArgs = formatArgs(args);
  const fullMessage = `${prefix} ${timestamp} [${level.toUpperCase()}] ${message}`;

  switch (level) {
    case 'debug':
      console.debug(fullMessage, ...formattedArgs);
      break;
    case 'info':
      console.info(fullMessage, ...formattedArgs);
      break;
    case 'warn':
      console.warn(fullMessage, ...formattedArgs);
      break;
    case 'error':
      console.error(fullMessage, ...formattedArgs);
      break;
  }
}

// ============================================================
// Public Logger API
// ============================================================

/**
 * Logger instance with methods for each log level.
 *
 * @example
 * ```typescript
 * import { logger, configureLogger } from './utils/logger';
 *
 * // Enable debug mode
 * configureLogger({ debug: true });
 *
 * // Use the logger
 * logger.debug('Processing landmarks', { count: 468 });
 * logger.info('Liveness action completed', { action: 'BLINK' });
 * logger.warn('Face not detected');
 * logger.error('Camera access denied', error);
 * ```
 */
export const logger = {
  /**
   * Debug level - detailed information for development.
   * Only shown when debug: true.
   */
  debug: (message: string, ...args: unknown[]): void => {
    logInternal('debug', message, ...args);
  },

  /**
   * Info level - general information about flow.
   * Only shown when debug: true.
   */
  info: (message: string, ...args: unknown[]): void => {
    logInternal('info', message, ...args);
  },

  /**
   * Warn level - potential issues that don't stop execution.
   * Always shown (even in production).
   */
  warn: (message: string, ...args: unknown[]): void => {
    logInternal('warn', message, ...args);
  },

  /**
   * Error level - errors that affect functionality.
   * Always shown (even in production).
   */
  error: (message: string, ...args: unknown[]): void => {
    logInternal('error', message, ...args);
  },

  /**
   * Log at a specific level.
   */
  log: (level: LogLevel, message: string, ...args: unknown[]): void => {
    logInternal(level, message, ...args);
  },

  /**
   * Create a child logger with a custom prefix.
   * Useful for module-specific logging.
   *
   * @example
   * ```typescript
   * const livenessLogger = logger.child('Liveness');
   * livenessLogger.debug('Starting blink detection');
   * // Output: [AppBouncer:Liveness] 2024-01-18T... [DEBUG] Starting blink detection
   * ```
   */
  child: (name: string) => ({
    debug: (message: string, ...args: unknown[]): void => {
      const prefix = `${loggerConfig.prefix || DEFAULT_PREFIX}:${name}`;
      const originalPrefix = loggerConfig.prefix;
      loggerConfig.prefix = prefix;
      logInternal('debug', message, ...args);
      loggerConfig.prefix = originalPrefix;
    },
    info: (message: string, ...args: unknown[]): void => {
      const prefix = `${loggerConfig.prefix || DEFAULT_PREFIX}:${name}`;
      const originalPrefix = loggerConfig.prefix;
      loggerConfig.prefix = prefix;
      logInternal('info', message, ...args);
      loggerConfig.prefix = originalPrefix;
    },
    warn: (message: string, ...args: unknown[]): void => {
      const prefix = `${loggerConfig.prefix || DEFAULT_PREFIX}:${name}`;
      const originalPrefix = loggerConfig.prefix;
      loggerConfig.prefix = prefix;
      logInternal('warn', message, ...args);
      loggerConfig.prefix = originalPrefix;
    },
    error: (message: string, ...args: unknown[]): void => {
      const prefix = `${loggerConfig.prefix || DEFAULT_PREFIX}:${name}`;
      const originalPrefix = loggerConfig.prefix;
      loggerConfig.prefix = prefix;
      logInternal('error', message, ...args);
      loggerConfig.prefix = originalPrefix;
    },
  }),
};

/**
 * Convenience function to quickly enable/disable debug mode.
 *
 * @example
 * ```typescript
 * setDebugMode(true);  // Enable all logging
 * setDebugMode(false); // Production mode (warn + error only)
 * ```
 */
export function setDebugMode(enabled: boolean): void {
  configureLogger({ debug: enabled });
}
